import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import AdminSidebar from '../components/AdminSidebar';
import {
  Folder,
  FolderOpen,
  File,
  ChevronUp,
  Plus,
  Pencil,
  Trash2,
  Move,
  RefreshCw,
  Loader2,
  Home,
  User,
  FileVideo,
  FileImage,
  FileText as FileTextIcon,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const getFileIcon = (name) => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (['mp4', 'mpg', 'mpeg', 'avi', 'mov', 'mkv', 'wmv'].includes(ext)) return FileVideo;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return FileImage;
  return FileTextIcon;
};

const AdminFolders = () => {
  const { token } = useAuth();
  const [currentPath, setCurrentPath] = useState('/app/uploads');
  const [parentPath, setParentPath] = useState(null);
  const [items, setItems] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [moveDest, setMoveDest] = useState('');
  const [moveFolders, setMoveFolders] = useState([]);
  const [moveCurrentPath, setMoveCurrentPath] = useState('/app/uploads');
  const [moveParentPath, setMoveParentPath] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const fetchContents = useCallback(async (path) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/folders/contents`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPath(response.data.current_path);
      setParentPath(response.data.parent_path);
      setItems(response.data.items);
      setAssignedUsers(response.data.assigned_users || {});
      setSelectedItem(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load folder');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchContents(currentPath);
  }, []);

  const navigateTo = (path) => fetchContents(path);

  const goUp = () => {
    if (parentPath) fetchContents(parentPath);
  };

  // CREATE
  const handleCreate = async () => {
    if (!newName.trim()) return;
    setIsActionLoading(true);
    try {
      await axios.post(`${API}/admin/folders/create`, null, {
        params: { path: currentPath, name: newName },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Folder "${newName}" created`);
      setIsCreateOpen(false);
      setNewName('');
      fetchContents(currentPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create folder');
    } finally {
      setIsActionLoading(false);
    }
  };

  // RENAME
  const openRename = (item) => {
    setSelectedItem(item);
    setNewName(item.name);
    setIsRenameOpen(true);
  };

  const handleRename = async () => {
    if (!newName.trim() || !selectedItem) return;
    setIsActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/folders/rename`, null, {
        params: { path: selectedItem.path, new_name: newName },
        headers: { Authorization: `Bearer ${token}` }
      });
      const msg = res.data.users_updated > 0
        ? `Renamed. ${res.data.users_updated} user assignment(s) updated.`
        : 'Folder renamed.';
      toast.success(msg);
      setIsRenameOpen(false);
      setNewName('');
      fetchContents(currentPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename');
    } finally {
      setIsActionLoading(false);
    }
  };

  // MOVE
  const openMove = (item) => {
    setSelectedItem(item);
    setMoveDest('');
    setMoveCurrentPath(currentPath);
    setIsMoveOpen(true);
    fetchMoveFolders(currentPath);
  };

  const fetchMoveFolders = async (path) => {
    try {
      const response = await axios.get(`${API}/admin/folders`, {
        params: { path },
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoveFolders(response.data.folders.filter(f => f.path !== selectedItem?.path));
      setMoveCurrentPath(response.data.current_path);
      setMoveParentPath(response.data.parent_path);
    } catch (error) {
      toast.error('Failed to load folders');
    }
  };

  const handleMove = async () => {
    if (!moveDest || !selectedItem) return;
    setIsActionLoading(true);
    try {
      const res = await axios.post(`${API}/admin/folders/move`, null, {
        params: { path: selectedItem.path, destination: moveDest },
        headers: { Authorization: `Bearer ${token}` }
      });
      const msg = res.data.users_updated > 0
        ? `Moved. ${res.data.users_updated} user assignment(s) updated.`
        : 'Folder moved.';
      toast.success(msg);
      setIsMoveOpen(false);
      fetchContents(currentPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move');
    } finally {
      setIsActionLoading(false);
    }
  };

  // DELETE
  const openDelete = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    setIsActionLoading(true);
    try {
      const res = await axios.delete(`${API}/admin/folders/delete`, {
        params: { path: selectedItem.path },
        headers: { Authorization: `Bearer ${token}` }
      });
      let msg = 'Folder deleted.';
      if (res.data.cleared_users?.length > 0) {
        msg += ` Cleared assignments for: ${res.data.cleared_users.join(', ')}`;
      }
      toast.success(msg);
      setIsDeleteOpen(false);
      fetchContents(currentPath);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setIsActionLoading(false);
    }
  };

  const folders = items.filter(i => i.is_dir);
  const files = items.filter(i => !i.is_dir);
  const pathParts = currentPath.split('/').filter(Boolean);

  return (
    <div className="min-h-screen bg-[#09090B] flex" data-testid="admin-folders">
      <AdminSidebar />

      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto pt-16 lg:pt-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="font-display text-2xl sm:text-4xl text-white tracking-tight">FOLDER MANAGEMENT</h1>
              <p className="text-zinc-500 mt-1 text-sm">Browse, rename, move, and delete NAS folders</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchContents(currentPath)}
                className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800">
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button size="sm" onClick={() => { setNewName(''); setIsCreateOpen(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-folder-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Folder</span>
                <span className="sm:hidden">New</span>
              </Button>
            </div>
          </div>

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 mb-4 p-3 bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-x-auto">
            <Button variant="ghost" size="sm" onClick={() => navigateTo('/app/uploads')}
              className="text-zinc-400 hover:text-white flex-shrink-0 h-7 px-2">
              <Home className="w-3 h-3" />
            </Button>
            {pathParts.map((part, i) => {
              const fullPath = '/' + pathParts.slice(0, i + 1).join('/');
              return (
                <span key={i} className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-zinc-600">/</span>
                  <button onClick={() => navigateTo(fullPath)}
                    className="text-xs font-mono text-zinc-400 hover:text-white transition-colors px-1">
                    {part}
                  </button>
                </span>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-2 mb-4">
            <Button variant="outline" size="sm" onClick={goUp} disabled={!parentPath || isLoading}
              className="border-zinc-700 text-zinc-400 hover:text-white" data-testid="folder-go-up">
              <ChevronUp className="w-4 h-4 mr-1" /> Up
            </Button>
          </div>

          {/* Contents */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <ScrollArea className="h-[600px]">
              {isLoading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-zinc-500">
                  <Folder className="w-12 h-12 mb-2 opacity-50" />
                  <p>Empty folder</p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800/50">
                  {/* Folders first */}
                  {folders.map(item => (
                    <div key={item.path}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/30 group transition-colors"
                      data-testid={`folder-${item.name}`}>
                      <button onClick={() => navigateTo(item.path)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        <FolderOpen className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">{item.name}</p>
                          <div className="flex items-center gap-2 text-xs text-zinc-500">
                            <span>{formatDate(item.modified)}</span>
                            {assignedUsers[item.path] && (
                              <span className="flex items-center gap-1 text-blue-400">
                                <User className="w-3 h-3" />
                                {assignedUsers[item.path]}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openRename(item)}
                          className="h-7 w-7 text-zinc-400 hover:text-white" data-testid={`rename-${item.name}`}
                          title="Rename">
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openMove(item)}
                          className="h-7 w-7 text-zinc-400 hover:text-white" data-testid={`move-${item.name}`}
                          title="Move">
                          <Move className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openDelete(item)}
                          className="h-7 w-7 text-zinc-400 hover:text-red-400" data-testid={`delete-${item.name}`}
                          title="Delete">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Files */}
                  {files.map(item => {
                    const Icon = getFileIcon(item.name);
                    return (
                      <div key={item.path}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/30 transition-colors"
                        data-testid={`file-${item.name}`}>
                        <Icon className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-zinc-300 text-sm truncate">{item.name}</p>
                          <div className="flex items-center gap-3 text-xs text-zinc-500">
                            <span>{formatBytes(item.size)}</span>
                            <span>{formatDate(item.modified)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Summary */}
          <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 px-1">
            <span>{folders.length} folder{folders.length !== 1 ? 's' : ''}</span>
            <span>{files.length} file{files.length !== 1 ? 's' : ''}</span>
            <span>{formatBytes(files.reduce((acc, f) => acc + (f.size || 0), 0))} total</span>
          </div>
        </div>
      </main>

      {/* Create Folder Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">NEW FOLDER</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-500 text-xs mb-2 font-mono">{currentPath}/</p>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              placeholder="Folder name" className="bg-zinc-800 border-zinc-700"
              autoFocus onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              data-testid="new-folder-name-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsCreateOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleCreate} disabled={isActionLoading || !newName.trim()}
              className="bg-blue-600 hover:bg-blue-700">
              {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">RENAME FOLDER</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-500 text-xs mb-3">
              Renaming <span className="text-white font-mono">{selectedItem?.name}</span>
              {assignedUsers[selectedItem?.path] && (
                <span className="text-blue-400 ml-1">(assigned to {assignedUsers[selectedItem?.path]})</span>
              )}
            </p>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)}
              className="bg-zinc-800 border-zinc-700" autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleRename()}
              data-testid="rename-folder-input" />
            {assignedUsers[selectedItem?.path] && (
              <p className="text-yellow-500 text-xs mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                User assignment will be automatically updated
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsRenameOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleRename} disabled={isActionLoading || !newName.trim()}
              className="bg-blue-600 hover:bg-blue-700">
              {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rename'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={isMoveOpen} onOpenChange={setIsMoveOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">MOVE FOLDER</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-500 text-xs mb-3">
              Moving <span className="text-white font-mono">{selectedItem?.name}</span> to:
            </p>
            <div className="p-2 bg-zinc-800/50 border border-zinc-700 rounded-lg mb-3">
              <p className="text-xs font-mono text-zinc-400 truncate">{moveCurrentPath}</p>
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Button variant="outline" size="sm" onClick={() => {
                if (moveParentPath) fetchMoveFolders(moveParentPath);
              }} disabled={!moveParentPath}
                className="border-zinc-700 text-zinc-400 hover:text-white text-xs">
                <ChevronUp className="w-3 h-3 mr-1" /> Up
              </Button>
              <Button variant="outline" size="sm" onClick={() => setMoveDest(moveCurrentPath)}
                className={`border-zinc-700 text-xs ${moveDest === moveCurrentPath ? 'bg-blue-600/20 text-blue-400 border-blue-600/50' : 'text-zinc-400 hover:text-white'}`}>
                Select this folder
              </Button>
            </div>
            <ScrollArea className="h-[200px] border border-zinc-800 bg-zinc-950/50 rounded-lg">
              {moveFolders.length === 0 ? (
                <div className="flex items-center justify-center h-full text-zinc-500 text-sm">No subfolders</div>
              ) : (
                <div className="p-2 space-y-1">
                  {moveFolders.map(folder => (
                    <button key={folder.path}
                      onClick={() => { setMoveDest(folder.path); }}
                      onDoubleClick={() => fetchMoveFolders(folder.path)}
                      className={`w-full flex items-center gap-2 p-2 text-left rounded transition-colors text-sm ${
                        moveDest === folder.path ? 'bg-blue-600/20 border border-blue-600/50' : 'hover:bg-zinc-800/50 border border-transparent'
                      }`}>
                      <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <span className="text-white truncate">{folder.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
            {moveDest && (
              <p className="text-xs text-zinc-400 mt-2">
                Destination: <span className="text-white font-mono">{moveDest}</span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsMoveOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleMove} disabled={isActionLoading || !moveDest}
              className="bg-blue-600 hover:bg-blue-700">
              {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Move Here'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-red-500">DELETE FOLDER</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-zinc-400 mb-3">
              Are you sure you want to delete <span className="text-white font-mono">{selectedItem?.name}</span> and all its contents?
            </p>
            {assignedUsers[selectedItem?.path] && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-xs mb-3">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  This folder is assigned to user: {assignedUsers[selectedItem?.path]}
                </div>
                <p>Their folder assignment will be cleared.</p>
              </div>
            )}
            <p className="text-red-400 text-xs">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsDeleteOpen(false)} className="text-zinc-400">Cancel</Button>
            <Button onClick={handleDelete} disabled={isActionLoading}
              variant="destructive" data-testid="confirm-delete-folder">
              {isActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Forever'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFolders;
